import React, { useState, useEffect } from 'react';
import { X, Plus, Trash2, AlertCircle } from 'lucide-react';
import api from '../services/api';
import { useAlert } from './AlertProvider';

interface UnitType {
    unit_id: number;
    unit_name: string;
    volume: number;
    product_count: number;
    consumable_ids: number[];
}

interface Tier {
    id?: number;
    tier_number: number;
    min_quantity: number;
    free_units?: number | null;
    discount_percentage?: number | null;
    volume?: number;
}

interface UnitTypeTierModalProps {
    isOpen: boolean;
    onClose: () => void;
    unitType: UnitType | null;
    bpuId: number;
    onSuccess: () => void;
}

const UnitTypeTierModal: React.FC<UnitTypeTierModalProps> = ({
    isOpen,
    onClose,
    unitType,
    bpuId,
    onSuccess
}) => {
    const { showSuccess, showError } = useAlert();
    const [tiers, setTiers] = useState<Tier[]>([]);
    const [tierMode, setTierMode] = useState<'ug' | 'percent'>('percent');
    const [loading, setLoading] = useState(false);
    const [selectedVolume, setSelectedVolume] = useState<number>(0);

    const effectiveVolume: number = selectedVolume || unitType?.volume || 0;

    useEffect(() => {
        if (isOpen && unitType) {
            // Load tiers directly into editor
            loadTiersDirectly();
        }
    }, [isOpen, unitType]);

    const loadTiersDirectly = async () => {
        if (!unitType) return;

        try {
            const response = await api.get(
                `/client-pricing/${bpuId}/unit-type-tiers?unit_id=${unitType.unit_id}&volume=${unitType.volume}`
            );

            if (response.data.tiers && response.data.tiers.length > 0) {
                // Load tiers directly into editor
                const tierList = response.data.tiers;
                setTiers(tierList);
                // Set volume from first tier
                setSelectedVolume(tierList[0]?.volume || 0);
                // Detect mode from first tier
                const firstTier = tierList[0];
                setTierMode(firstTier.free_units !== null && firstTier.free_units !== undefined ? 'ug' : 'percent');
            } else {
                setTiers([]);
                setSelectedVolume(unitType.volume);
            }
        } catch (error) {
            console.error('Error loading tiers:', error);
            setTiers([]);
            setSelectedVolume(unitType.volume);
        }
    };

    if (!isOpen || !unitType) return null;

    const addTier = () => {
        const newTierNumber = tiers.length + 1;
        const lastTier = tiers[tiers.length - 1];
        const newMinQty = lastTier ? lastTier.min_quantity + 1 : 2; // In LOTS, not units

        setTiers([...tiers, {
            tier_number: newTierNumber,
            min_quantity: newMinQty,
            free_units: tierMode === 'ug' ? (lastTier?.free_units || 0) + 6 : null,
            discount_percentage: tierMode === 'percent' ? (lastTier?.discount_percentage || 0) + 5 : null
        }]);
    };

    const removeTier = async (index: number) => {
        const tierToDelete = tiers[index];

        // If tier has an ID, delete from backend
        if (tierToDelete.id) {
            try {
                await api.delete(`/client-pricing/unit-tier/${tierToDelete.id}`);
                showSuccess('Palier supprimé');
            } catch (error: any) {
                showError(error.response?.data?.detail || 'Erreur lors de la suppression');
                return;
            }
        }

        // Remove from local state
        const newTiers = tiers.filter((_, i) => i !== index);
        // Renumber tiers
        newTiers.forEach((tier, i) => {
            tier.tier_number = i + 1;
        });
        setTiers(newTiers);
    };

    const updateTier = (index: number, field: keyof Tier, value: any) => {
        const newTiers = [...tiers];
        newTiers[index] = { ...newTiers[index], [field]: value };
        setTiers(newTiers);
    };

    const handleModeChange = (mode: 'ug' | 'percent') => {
        setTierMode(mode);
        // Clear all existing tiers when switching modes
        // Admin must choose only one tier type (UG or %)
        setTiers([]);
    };

    const handleDeleteAll = async () => {
        if (!unitType) return;

        setLoading(true);
        try {
            await api.delete(
                `/client-pricing/${bpuId}/unit-type-tiers?unit_id=${unitType.unit_id}&volume=${effectiveVolume}`
            );

            showSuccess(`Configuration supprimée pour "${unitType.unit_name}"`);
            onSuccess();
            onClose();
        } catch (error: any) {
            showError(error.response?.data?.detail || 'Erreur lors de la suppression');
        } finally {
            setLoading(false);
        }
    };

    const handleApply = async () => {
        // Validate tiers
        if (tiers.length === 0) {
            showError('Veuillez ajouter au moins un palier');
            return;
        }

        for (const tier of tiers) {
            if (tier.min_quantity <= 0) {
                showError('La quantité minimale doit être supérieure à 0');
                return;
            }
            if (tierMode === 'ug' && (!tier.free_units || tier.free_units <= 0)) {
                showError('Les unités gratuites doivent être supérieures à 0');
                return;
            }
            if (tierMode === 'percent' && (!tier.discount_percentage || tier.discount_percentage <= 0)) {
                showError('Le pourcentage de remise doit être supérieur à 0');
                return;
            }
        }

        setLoading(true);
        try {
            await api.post(
                `/client-pricing/${bpuId}/apply-to-unit-type?unit_id=${unitType.unit_id}&volume=${effectiveVolume}`,
                tiers
            );

            showSuccess(`Paliers appliqués à tous les produits de type "${unitType.unit_name}"`);
            onSuccess();
            onClose();
        } catch (error: any) {
            showError(error.response?.data?.detail || 'Erreur lors de l\'application des paliers');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white dark:bg-slate-800 rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col">
                {/* Header */}
                <div className="flex items-center justify-between p-3 sm:p-6 border-b border-slate-200 dark:border-slate-700 gap-2">
                    <div className="min-w-0">
                        <h2 className="text-sm sm:text-xl font-semibold text-slate-900 dark:text-white">
                            Configuration des paliers - {unitType.unit_name}
                        </h2>
                    </div>
                    <div className="flex items-center gap-1 sm:gap-2 shrink-0">
                        {tiers.length > 0 && (
                            <button
                                onClick={handleDeleteAll}
                                disabled={loading}
                                className="flex items-center gap-1 sm:gap-2 px-2 sm:px-3 py-1.5 sm:py-2 text-xs sm:text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors disabled:opacity-50"
                                title="Supprimer tous les paliers"
                            >
                                <Trash2 className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                                <span className="hidden sm:inline">Tout supprimer</span>
                            </button>
                        )}
                        <button
                            onClick={onClose}
                            className="p-1.5 sm:p-2 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
                        >
                            <X className="w-5 h-5" />
                        </button>
                    </div>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-3 sm:p-6 space-y-4 sm:space-y-6">
                    {/* Info box */}
                    <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3 sm:p-4 flex gap-2 sm:gap-3">
                        <AlertCircle className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                        <div className="text-xs sm:text-sm text-blue-800 dark:text-blue-200">
                            <p className="font-medium mb-0.5 sm:mb-1">Application automatique</p>
                            <p>Les paliers configurés seront appliqués à tous les produits de type "{unitType.unit_name}".</p>
                        </div>
                    </div>

                    {/* Mode selection - UG hidden for now */}
                    {false && (
                        <div>
                            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                                Type de palier
                            </label>
                            <div className="flex gap-2">
                                <button
                                    onClick={() => handleModeChange('ug')}
                                    className={`flex-1 px-4 py-2 rounded-lg border-2 transition-colors ${tierMode === 'ug'
                                        ? 'border-orange-500 bg-orange-50 dark:bg-orange-900/20 text-orange-700 dark:text-orange-300'
                                        : 'border-slate-300 dark:border-slate-600 hover:border-slate-400'
                                        }`}
                                >
                                    <div className="font-medium">Unités Gratuites (UG)</div>
                                </button>
                                <button
                                    onClick={() => handleModeChange('percent')}
                                    className={`flex-1 px-4 py-2 rounded-lg border-2 transition-colors ${tierMode === 'percent'
                                        ? 'border-orange-500 bg-orange-50 dark:bg-orange-900/20 text-orange-700 dark:text-orange-300'
                                        : 'border-slate-300 dark:border-slate-600 hover:border-slate-400'
                                        }`}
                                >
                                    <div className="font-medium">Remise en %</div>
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Tiers configuration */}
                    <div>
                        <div className="flex items-center justify-between mb-2 sm:mb-3">
                            <label className="block text-xs sm:text-sm font-medium text-slate-700 dark:text-slate-300">
                                Paliers
                            </label>
                            <button
                                onClick={addTier}
                                className="flex items-center gap-1 px-2.5 sm:px-3 py-1 sm:py-1.5 text-xs sm:text-sm bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
                            >
                                <Plus className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                                Ajouter un palier
                            </button>
                        </div>

                        {tiers.length === 0 ? (
                            <div className="text-center py-8 text-slate-500 dark:text-slate-400">
                                <p className="text-sm">Aucun palier configuré</p>
                                <p className="text-xs mt-1">Cliquez sur "Ajouter un palier" pour commencer</p>
                            </div>
                        ) : (
                            <div className="space-y-2 sm:space-y-3">
                                {tiers.map((tier, index) => (
                                    <div
                                        key={index}
                                        className="border border-slate-200 dark:border-slate-700 rounded-lg p-2.5 sm:p-4 bg-slate-50 dark:bg-slate-900/50"
                                    >
                                        <div className="flex items-center gap-2 sm:gap-4">
                                            <div className="flex-shrink-0 w-12 sm:w-20">
                                                <label className="block text-[10px] sm:text-xs text-slate-500 mb-0.5 sm:mb-1">Palier</label>
                                                <div className="text-base sm:text-lg font-semibold text-orange-600">#{tier.tier_number}</div>
                                            </div>

                                            <div className="flex-1 grid grid-cols-2 gap-2 sm:gap-4">
                                                <div>
                                                    <label className="block text-[10px] sm:text-xs text-slate-500 mb-0.5 sm:mb-1">
                                                        Qté min (lots)
                                                    </label>
                                                    <input
                                                        type="number"
                                                        value={tier.min_quantity}
                                                        onChange={(e) => updateTier(index, 'min_quantity', parseInt(e.target.value) || 0)}
                                                        className="w-full px-2 sm:px-3 py-1.5 sm:py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800"
                                                        min="1"
                                                        step="1"
                                                    />
                                                </div>

                                                <div>
                                                    <label className="block text-[10px] sm:text-xs text-slate-500 mb-0.5 sm:mb-1">
                                                        {tierMode === 'ug' ? 'Unités Gratuites' : 'Remise (%)'}
                                                    </label>
                                                    <input
                                                        type="number"
                                                        value={tierMode === 'ug' ? (tier.free_units || 0) : (tier.discount_percentage || 0)}
                                                        onChange={(e) => {
                                                            const field = tierMode === 'ug' ? 'free_units' : 'discount_percentage';
                                                            updateTier(index, field, parseFloat(e.target.value) || 0);
                                                        }}
                                                        className="w-full px-2 sm:px-3 py-1.5 sm:py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800"
                                                        min="0"
                                                        step={tierMode === 'ug' ? 1 : 5}
                                                    />
                                                </div>
                                            </div>

                                            <button
                                                onClick={() => removeTier(index)}
                                                className="flex-shrink-0 p-1.5 sm:p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                                                title="Supprimer ce palier"
                                            >
                                                <Trash2 className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                {/* Footer */}
                <div className="flex items-center justify-end gap-2 sm:gap-3 p-3 sm:p-6 border-t border-slate-200 dark:border-slate-700">
                    <button
                        onClick={onClose}
                        className="px-3 sm:px-4 py-2 text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
                        disabled={loading}
                    >
                        Annuler
                    </button>
                    <button
                        onClick={handleApply}
                        disabled={loading || tiers.length === 0}
                        className="px-4 sm:px-6 py-2 text-sm bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors disabled:bg-slate-400 disabled:cursor-not-allowed flex items-center gap-2"
                    >
                        {loading ? (
                            <>
                                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                Application...
                            </>
                        ) : (
                            'Appliquer à tous'
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default UnitTypeTierModal;
